<script setup>
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";
import {useAuthService} from "~/composables/useAuthService";

// Compact club rows for the app-bar search dialog. The clubs grid uses the taller
// ClubClickableCell card; here we want short, scannable rows (crest · name · meta ·
// chevron) in a single card, mirroring PlayerList.
const props = defineProps({
  items: {type: Array, default: () => []},
});

const {convertAddress} = useCrudConverters();
const {isAuthenticated, isMemberOfClub, isAdminOfClub} = useAuthService();

const initials = (club) =>
    (club?.name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const meta = (club) => {
  const parts = [];
  if (club?.playerCount != null) parts.push(`${club.playerCount} player${club.playerCount === 1 ? '' : 's'}`);
  if (club?.city) parts.push(club.city);
  else if (club?.hasFixedAddress) parts.push(convertAddress(club));
  return parts.join(' · ');
};

const relationship = (club) => {
  if (!isAuthenticated.value) return null;
  if (isAdminOfClub(club?.id)) return 'Admin';
  if (isMemberOfClub(club?.id)) return 'Joined';
  return null;
};
</script>

<template>
  <v-list class="pa-0 tl-card club-search-list">
    <template v-for="(club, index) in items" :key="club.id">
      <router-link class="csr" :to="{ name: 'ClubDetail', params: { clubId: club.slug } }">
        <avatar-image-loader
            v-if="club?.imageFileName"
            :image-file-name="club.imageFileName"
            :image-clickable="false"
            size="40"
            class="csr-crest flex-shrink-0"
        />
        <span v-else class="csr-crest csr-crest--fallback flex-shrink-0">{{ initials(club) }}</span>

        <div class="csr-who">
          <div class="csr-name">
            <span class="csr-name-text">{{ club.name }}</span>
            <span v-if="relationship(club)" class="csr-chip">{{ relationship(club) }}</span>
          </div>
          <div v-if="meta(club)" class="csr-meta">{{ meta(club) }}</div>
        </div>

        <v-icon size="20" class="csr-chev">mdi-chevron-right</v-icon>
      </router-link>
      <v-divider v-if="index < items.length - 1"/>
    </template>
  </v-list>
</template>

<style scoped lang="scss">
.club-search-list :deep(.v-divider) {
  border-color: rgb(var(--v-theme-borderLight));
  opacity: 1;
}

.csr {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  transition: background 0.12s ease;

  &:hover {
    background: rgb(var(--v-theme-light));

    .csr-chev {
      color: rgb(var(--v-theme-primary));
      transform: translateX(2px);
    }
  }
}

.csr-crest {
  width: 40px;
  height: 40px;
  border-radius: 10px !important;
}

.csr-crest--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  font-weight: 800;
}

.csr-who {
  min-width: 0;
  flex: 1;
}

.csr-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.csr-name-text {
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: rgb(var(--v-theme-text));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.csr-chip {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.csr-meta {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.csr-chev {
  flex-shrink: 0;
  color: rgb(var(--v-theme-textSubtle));
  transition: transform 0.12s ease, color 0.12s ease;
}
</style>
